import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { clearErrors, getAllOrders } from '../../actions/orderAction';
import { formatDate } from '../../utils/functions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const statusChip = (status) => {
    if (status === "Delivered") return <span className="text-sm bg-green-100 px-2 py-0.5 font-medium rounded-full text-green-800">{status}</span>;
    if (status === "Shipped") return <span className="text-sm bg-yellow-100 px-2 py-0.5 font-medium rounded-full text-yellow-800">{status}</span>;
    return <span className="text-sm bg-purple-100 px-2 py-0.5 font-medium rounded-full text-purple-800">{status}</span>;
};

const CustomToolbar = () => (
    <GridToolbarContainer className="p-3 flex flex-wrap gap-2 items-center justify-between border-b">
        <div className="flex gap-1">
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </div>
        <GridToolbarQuickFilter placeholder="Search orders..." />
    </GridToolbarContainer>
);

const OrderTable = ({ legacy = false, trash = false, userId = null, customerName = null }) => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { orders, error } = useSelector((state) => state.allOrders);
    const { loading } = useSelector((state) => state.order);
    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const orderPerms = user.adminRole?.permissions?.orders || {};
    const canEdit = isSuperAdmin || orderPerms.edit;

    const [pageSize, setPageSize] = useState(25);
    const [customerFilter, setCustomerFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [phoneFilter, setPhoneFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        dispatch(getAllOrders(legacy, trash));
    }, [dispatch, error, enqueueSnackbar, legacy, trash]);

    const bulkDeleteHandler = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedIds.length} selected order(s)? This moves them to Trash and excludes them from all sales stats.`)) return;

        setBulkDeleting(true);
        try {
            await axios.post('/api/v1/admin/orders/bulk-delete', { orderIds: selectedIds });
            enqueueSnackbar(`${selectedIds.length} order(s) moved to Trash`, { variant: "success" });
            setSelectedIds([]);
            dispatch(getAllOrders(legacy, trash));
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to delete selected orders", { variant: "error" });
        } finally {
            setBulkDeleting(false);
        }
    };

    const customerOptions = useMemo(() => {
        const names = (orders || []).map((o) => o.user?.name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [orders]);

    const locationOptions = useMemo(() => {
        const cities = (orders || []).map((o) => o.shippingInfo?.city).filter(Boolean);
        return [...new Set(cities)].sort();
    }, [orders]);

    const filteredOrders = useMemo(() => {
        return (orders || []).filter((order) => {
            if (userId && order.user?._id !== userId) return false;
            if (customerFilter && order.user?.name !== customerFilter) return false;
            if (locationFilter && order.shippingInfo?.city !== locationFilter) return false;
            if (phoneFilter && !String(order.shippingInfo?.phoneNo || "").includes(phoneFilter)) return false;
            if (statusFilter && order.orderStatus !== statusFilter) return false;
            if (fromDate && new Date(order.createdAt) < new Date(fromDate)) return false;
            if (toDate && new Date(order.createdAt) > new Date(toDate + "T23:59:59")) return false;
            return true;
        });
    }, [orders, userId, customerFilter, locationFilter, phoneFilter, statusFilter, fromDate, toDate]);

    const activeFilters = [
        customerFilter && { key: "customer", label: `Customer: ${customerFilter}`, clear: () => setCustomerFilter("") },
        locationFilter && { key: "location", label: `Location: ${locationFilter}`, clear: () => setLocationFilter("") },
        phoneFilter && { key: "phone", label: `Phone: ${phoneFilter}`, clear: () => setPhoneFilter("") },
        statusFilter && { key: "status", label: `Status: ${statusFilter}`, clear: () => setStatusFilter("") },
        fromDate && { key: "from", label: `From: ${fromDate}`, clear: () => setFromDate("") },
        toDate && { key: "to", label: `To: ${toDate}`, clear: () => setToDate("") },
    ].filter(Boolean);

    const columns = [
        {
            field: "orderNumber",
            headerName: "Order ID",
            minWidth: 120,
            flex: 0.4,
            renderCell: (params) => (
                <Link to={`/admin/order/${params.row.id}`} className="text-primary-blue hover:underline font-medium">
                    #{params.row.orderNumber}
                </Link>
            ),
        },
        {
            field: "customer",
            headerName: "Customer",
            minWidth: 160,
            flex: 0.5,
        },
        {
            field: "phone",
            headerName: "Phone",
            minWidth: 130,
            flex: 0.4,
        },
        {
            field: "location",
            headerName: "Location",
            minWidth: 140,
            flex: 0.4,
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 130,
            flex: 0.3,
            renderCell: (params) => statusChip(params.row.status),
        },
        {
            field: "itemsQty",
            headerName: "Items Qty",
            type: "number",
            minWidth: 100,
            flex: 0.2,
        },
        {
            field: "amount",
            headerName: "Amount",
            type: "number",
            minWidth: 120,
            flex: 0.3,
            renderCell: (params) => <span>HK${params.row.amount.toLocaleString()}</span>,
        },
        {
            field: "paymentMode",
            headerName: "Payment Mode",
            minWidth: 140,
            flex: 0.35,
            renderCell: (params) => (
                params.row.paymentMode === 'card' ? (
                    <div className="leading-tight">
                        <div className="font-medium">Stripe</div>
                        <div className="text-xs text-gray-500">Credit card</div>
                    </div>
                ) : (
                    <span className="font-medium">COD</span>
                )
            ),
        },
        {
            field: "orderSource",
            headerName: "Order Mode",
            minWidth: 130,
            flex: 0.3,
            renderCell: (params) => (
                <span className="text-sm bg-gray-100 px-2 py-0.5 font-medium rounded-full text-gray-700">
                    {params.row.orderSource === 'customer_app' ? 'Mobile App' : 'Website'}
                </span>
            ),
        },
        {
            field: "wallet",
            headerName: "1313 Wallet",
            minWidth: 130,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <div className="flex flex-col text-xs">
                    {params.row.pointsEarned > 0 && <span className="text-primary-orange font-medium">+{params.row.pointsEarned.toLocaleString()} pts</span>}
                    {params.row.walletUsed && <span className="text-primary-green font-medium">Used</span>}
                    {!params.row.pointsEarned && !params.row.walletUsed && <span className="text-gray-300">—</span>}
                </div>
            ),
        },
        {
            field: "orderOn",
            headerName: "Order On",
            type: "date",
            minWidth: 150,
            flex: 0.4,
            renderCell: (params) => formatDate(params.row.orderOn),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 90,
            flex: 0.2,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                canEdit ? <Link to={`/admin/order/${params.row.id}`} className="text-primary-blue hover:bg-blue-50 p-1.5 rounded-full"><EditIcon sx={{ fontSize: "20px" }} /></Link> : null
            ),
        },
    ];

    const rows = filteredOrders.map((order) => ({
        id: order._id,
        orderNumber: order.orderNumber ?? "—",
        customer: order.user?.name || "—",
        phone: order.shippingInfo?.phoneNo || "—",
        location: order.shippingInfo?.city ? `${order.shippingInfo.city}, ${order.shippingInfo.state}` : "—",
        itemsQty: order.orderItems.length,
        amount: order.totalPrice,
        paymentMode: order.paymentMethod || 'cod',
        orderSource: order.orderSource || 'website',
        pointsEarned: order.pointsEarned || 0,
        walletUsed: (order.pointsRedeemed > 0 || order.walletCashUsed > 0),
        orderOn: new Date(order.createdAt),
        status: order.orderStatus,
    }));

    return (
        <>
            <MetaData title={trash ? "Admin: Trash | 1313 Mart" : legacy ? "Admin: Legacy Orders | 1313 Mart" : "Admin Orders | 1313 Mart"} />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">
                    {userId ? `Order History${customerName ? ` — ${customerName}` : ''}` : trash ? "Trash" : (legacy ? "Legacy Orders (Before Migration)" : "Orders")}
                </h1>
                <div className="flex gap-2">
                    {userId ? (
                        <Link to="/admin/users" className="py-2 px-4 rounded shadow font-medium text-primary-blue border border-primary-blue hover:bg-primary-blue hover:text-white">&larr; Back to Users</Link>
                    ) : trash ? (
                        <Link to="/admin/orders" className="py-2 px-4 rounded shadow font-medium text-primary-blue border border-primary-blue hover:bg-primary-blue hover:text-white">&larr; Back to Orders</Link>
                    ) : legacy ? (
                        <Link to="/admin/orders" className="py-2 px-4 rounded shadow font-medium text-primary-blue border border-primary-blue hover:bg-primary-blue hover:text-white">Back to Current Orders</Link>
                    ) : (
                        <>
                            {isSuperAdmin && (
                                <Link to="/admin/orders/trash" className="py-2 px-4 rounded shadow font-medium text-gray-600 border hover:bg-gray-50">Trash</Link>
                            )}
                            <Link to="/admin/orders/legacy" className="py-2 px-4 rounded shadow font-medium text-gray-600 border hover:bg-gray-50">View Legacy Orders</Link>
                        </>
                    )}
                </div>
            </div>

            {isSuperAdmin && !userId && selectedIds.length > 0 && (
                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded p-3 mb-4">
                    <span className="text-sm text-gray-700">{selectedIds.length} order(s) selected</span>
                    <button
                        onClick={bulkDeleteHandler}
                        disabled={bulkDeleting}
                        className="text-sm bg-red-600 text-white font-medium px-4 py-2 rounded shadow hover:shadow-lg disabled:opacity-60"
                    >
                        {bulkDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
                    </button>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">

                <div className="flex flex-wrap items-end gap-3 p-4 border-b">
                    <TextField label="Customer" select size="small" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All Customers</MenuItem>
                        {customerOptions.map((name) => <MenuItem value={name} key={name}>{name}</MenuItem>)}
                    </TextField>
                    <TextField label="Phone" size="small" value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} sx={{ minWidth: 150 }} placeholder="Search phone..." />
                    <TextField label="Location" select size="small" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All Locations</MenuItem>
                        {locationOptions.map((city) => <MenuItem value={city} key={city}>{city}</MenuItem>)}
                    </TextField>
                    <TextField label="Status" select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="Processing">Processing</MenuItem>
                        <MenuItem value="Accepted">Accepted</MenuItem>
                        <MenuItem value="Packed">Packed</MenuItem>
                        <MenuItem value="Shipped">Shipped</MenuItem>
                        <MenuItem value="Delivered">Delivered</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </TextField>
                    <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ minWidth: 150 }} />
                    <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ minWidth: 150 }} />
                </div>

                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-gray-50">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {activeFilters.map((f) => (
                            <Chip key={f.key} label={f.label} size="small" onDelete={f.clear} />
                        ))}
                        <button
                            onClick={() => { setCustomerFilter(""); setLocationFilter(""); setPhoneFilter(""); setStatusFilter(""); setFromDate(""); setToDate(""); }}
                            className="text-xs text-primary-blue hover:underline ml-1"
                        >
                            Clear All
                        </button>
                    </div>
                )}

                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        rowHeight={64}
                        pageSize={pageSize}
                        initialState={{ sorting: { sortModel: [{ field: 'orderOn', sort: 'desc' }] } }}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        disableSelectIconOnClick
                        checkboxSelection={isSuperAdmin && !userId}
                        onSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
                        selectionModel={selectedIds}
                        components={{ Toolbar: CustomToolbar }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default OrderTable;